import { createContext, useContext, useEffect, useMemo } from "react";
import { SolicitorNotification, SolicitorNotificationRequest } from "../models/classes/solicitor/SolicitorNotification.class";
import { useAppSelector } from "../redux/hook";
import {
  useGetSolicitorCaseSummaryQuery,
  useGetSolicitorCompletionDetailsQuery,
  useGetSolicitorNotificationQuery,
  useGetSolicitorSubmissionsQuery,
} from "../redux/services/getSolicitor";
import { SolicitorCompletionDetail } from "../models/classes/solicitor/SolicitorCompletionDetail.class";
import { SolicitorNotificationStatusEnum } from "../models/enum/solicitor-notification-status.enum";
import { API_Solicitor_Notification } from "../utils/api";
import { PolymerAxios } from "../utils/axios";
import { SolicitorCaseSummary } from "../models/classes/solicitor/SolicitorCaseSummary.class";
import { SolicitorSubmission } from "../models/classes/solicitor/SolicitorSubmission.class";

export interface SolicitorDetailsContextInterface {
  solicitorSubmissionsData?: SolicitorSubmission;
  solicitorSubmissionsIsSuccess: boolean;
  solicitorSubmissionsIsFetching: boolean;
  solicitorSubmissionsRefetch: () => void;
  solicitorCaseSummaryData?: SolicitorCaseSummary;
  solicitorCaseSummaryIsSuccess: boolean;
  solicitorCaseSummaryIsFetching: boolean;
  isCancelledOrRejected: boolean;
  solcitorCaseSummaryRefetch: () => void;
  solicitorCompletionData?: SolicitorCompletionDetail;
  solicitorCompletionIsFetching: boolean;
  isShowingCompletion: boolean;
  isShowingLodgement: boolean;
  solicitorCompletionRefetch: () => void;
  solicitorNotificationData?: SolicitorNotification;
  solicitorNotificationIsSuccess: boolean;
  solicitorNotificationIsFetching: boolean;
  solicitorCompletionIsSuccess: boolean;
  solicitorNotificationRefetch: () => void;
}

export const SolicitorDetailsContext = createContext<SolicitorDetailsContextInterface>({} as SolicitorDetailsContextInterface);

interface SolicitorCaseDetailsProviderInterface {
  children: React.ReactNode;
}

export const useSolicitorCaseDetailsContext = () => {
  return useContext(SolicitorDetailsContext);
};

export const SolicitorCaseDetailsProvider = ({ children }: SolicitorCaseDetailsProviderInterface) => {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const corpPassDetails = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);

  const {
    data: solicitorSubmissionsData,
    isSuccess: solicitorSubmissionsIsSuccess,
    isFetching: solicitorSubmissionsIsFetching,
    refetch: solicitorSubmissionsRefetch,
  } = useGetSolicitorSubmissionsQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen: corpPassDetails.uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  const {
    data: solicitorCaseSummaryData,
    isSuccess: solicitorCaseSummaryIsSuccess,
    isFetching: solicitorCaseSummaryIsFetching,
    refetch: solcitorCaseSummaryRefetch,
  } = useGetSolicitorCaseSummaryQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen: corpPassDetails.uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  const {
    data: solicitorNotificationData,
    isSuccess: solicitorNotificationIsSuccess,
    isFetching: solicitorNotificationIsFetching,
    refetch: solicitorNotificationRefetch,
  } = useGetSolicitorNotificationQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen: corpPassDetails.uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  const {
    data: solicitorCompletionData,
    isSuccess: solicitorCompletionIsSuccess,
    isFetching: solicitorCompletionIsFetching,
    refetch: solicitorCompletionRefetch,
  } = useGetSolicitorCompletionDetailsQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen: corpPassDetails.uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  // Derived value
  const isCancelledOrRejected = useMemo(
    () =>
      solicitorCaseSummaryData?.application_status === "APPLICATION_CANCELLED" ||
      solicitorCaseSummaryData?.application_status === "CANCELLED" ||
      solicitorCaseSummaryData?.application_status === "REJECTED",
    [solicitorCaseSummaryData]
  );

  const isShowingCompletion = !!solicitorCompletionData?.can_edit && solicitorCompletionData?.status === "PENDING_COMPLETION_DETAIL";
  const isShowingLodgement = !!solicitorCompletionData?.can_edit && solicitorCompletionData?.status === "PENDING_LODGEMENT_DETAIL";

  // Side effects
  useEffect(() => {
    if (solicitorNotificationData && solicitorNotificationData?.notification_status !== SolicitorNotificationStatusEnum.no_action) {
      PolymerAxios(loginUser?.uinfin ?? "", singpassToken, corpPassDetails.uen).patch(API_Solicitor_Notification, undefined, {
        params: { applicationUuid: applicationUuid },
      });
    }
  }, [solicitorNotificationData]);

  return (
    <SolicitorDetailsContext.Provider
      value={{
        solicitorSubmissionsData,
        solicitorSubmissionsIsSuccess,
        solicitorSubmissionsIsFetching,
        solicitorSubmissionsRefetch,
        solicitorCaseSummaryData,
        solicitorCaseSummaryIsSuccess,
        solicitorCaseSummaryIsFetching,
        isCancelledOrRejected,
        solcitorCaseSummaryRefetch,
        solicitorCompletionData,
        solicitorCompletionIsSuccess,
        solicitorCompletionIsFetching,
        isShowingCompletion,
        isShowingLodgement,
        solicitorCompletionRefetch,
        solicitorNotificationData,
        solicitorNotificationIsSuccess,
        solicitorNotificationIsFetching,
        solicitorNotificationRefetch,
      }}
    >
      {children}
    </SolicitorDetailsContext.Provider>
  );
};
